/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import React from 'react';
import { forwardRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { convertToRaw, EditorState, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import clsx from 'clsx';
import Typography from '@mui/material/Typography';

const Root = styled('div')({
  '& .rdw-dropdown-selectedtext': {
    color: 'inherit'
  },
  '& .rdw-editor-toolbar': {
    borderWidth: '0 0 1px 0!important',
    margin: '0!important'
  },
  '& .rdw-editor-main': {
    padding: '8px 12px',
    height: `${256}px!important`
  }
});

const WYSIWYGEditor = forwardRef((props, ref) => {
  const { value, onChange, className, error, allowGreen = false } = props;

  const colorOptions = ['rgb(247,218,100)']; // always allow yellow
  if (allowGreen) {
    colorOptions.push('rgb(97,189,109)');
  }

  const [editorState, setEditorState] = useState(
    EditorState.createWithContent(
      ContentState.createFromBlockArray(htmlToDraft(value).contentBlocks)
    )
  );

  const onEditorStateChange = (_editorState) => {
    setEditorState(_editorState);

    return onChange(
      draftToHtml(convertToRaw(_editorState.getCurrentContent()))
    );
  };

  return (
    <div className="mb-32">
      <Root
        className={clsx(
          'rounded-4 border-1 overflow-hidden w-full mt-16 mb-0',
          className,
          { 'border-red-500': error }
        )}
        ref={ref}
      >
        <Editor
          editorState={editorState}
          onEditorStateChange={onEditorStateChange}
          toolbar={{
            options: [
              'inline',
              'fontSize',
              'list',
              'textAlign',
              'colorPicker',
              'link',
              'history'
            ],
            inline: {
              options: [
                'bold',
                'italic',
                'underline',
                'superscript',
                'subscript'
              ]
            },
            fontSize: {
              options: [10, 12, 14, 16, 18, 24]
            },
            list: {
              inDropdown: true
            },
            textAlign: {
              inDropdown: true,
              options: ['left', 'center', 'right']
            },
            colorPicker: {
              colors: colorOptions
            },
            link: {
              inDropdown: true
            }
          }}
          stripPastedStyles
        />
      </Root>
      {error && (
        <Typography variant="caption" color="error" className="mx-14">
          {error.message}
        </Typography>
      )}
    </div>
  );
});

export default WYSIWYGEditor;
